/**
 * Checks if an array is non-empty.
 */
export function isNonEmpty<T>(items: T[]): items is [T, ...T[]] {
  return items.length > 0;
}

type TupleOf<
  T,
  N extends number,
  R extends unknown[] = [],
> = R['length'] extends N ? R : TupleOf<T, N, [T, ...R]>;

/**
 * Checks if an array has a specific length, narrowing the type to a tuple of
 * the given length.
 */
export function isLength<T, N extends number>(
  arr: readonly T[],
  length: N
): arr is TupleOf<T, N> {
  return arr.length === length;
}

/**
 * Converts a value to an array, if it is not already an array.
 *
 * @example
 * toArray(1); // [1]
 * toArray([1]); // [1]
 *
 * @param value - The value to convert.
 * @returns An array.
 */
export function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

/**
 * Splits an array into chunks of the given size.
 *
 * @example
 * chunk([1, 2, 3, 4, 5], 2); // [[1, 2], [3, 4], [5]]
 *
 * @param arr - The array to chunk.
 * @param size - The size of each chunk.
 * @returns An array of chunks.
 */
export function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}

/**
 * Creates a function that returns an item from the provided array for a given
 * string, based on a stable hash of the string.
 *
 * @example
 * const getColor = createStableKeySelector(['red', 'green', 'blue']);
 * getColor('Albert'); // 'blue'
 * getColor('Barbara'); // 'green'
 * getColor('Charlie'); // 'red'
 *
 * @param keys - The keys to use for the function.
 * @returns A function that returns an item from the provided array.
 */
export function createStableKeySelector<const T extends readonly string[]>(
  keys: T
) {
  if (keys.length === 0) {
    throw new Error('Requires at least one key.');
  }

  return function selectKeyFromStableHash(value: string): T[number] {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      // `(hash << 5) - hash` yields an odd prime, which avoids overflow / loss
      // of information when using modulo operation
      hash = value.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % keys.length;
    return keys[index]!;
  };
}
